// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { getCitationSettings, toggleCitationSettings } from '../../services/api';

const CitationSettings = ({ clientId, onStatusChange, onSaveClick }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [citationsEnabled, setCitationsEnabled] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [clientId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await getCitationSettings(clientId);

      if (data.success) {
        setCitationsEnabled(data.citations_enabled);
        // Notify parent component of current status
        if (onStatusChange) {
          onStatusChange(data.citations_enabled);
        }
      } else {
        toast.error(data.message || 'Failed to load citation settings');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCitations = async (event) => {
    const newState = event.target.checked;

    try {
      // Set local state immediately for responsive UI
      setCitationsEnabled(newState);
      setSaving(true);

      // Call the onSaveClick callback to notify parent component
      if (onSaveClick) {
        onSaveClick();
      }

      console.log('[CitationSettings] Saving settings, setting enabled to:', newState);

      const data = await toggleCitationSettings(clientId, newState);

      if (data.success) {
        // Update the local state based on the response from the server
        const serverEnabledState = data.citations_enabled;
        console.log('[CitationSettings] Server returned citations_enabled:', serverEnabledState);

        // If server state doesn't match what we sent, update our local state to match server
        if (serverEnabledState !== newState) {
          console.warn('[CitationSettings] Server state differs from local state, updating local state');
          setCitationsEnabled(serverEnabledState);
        }

        toast.success(`Citations ${serverEnabledState ? 'enabled' : 'disabled'} successfully`);

        // Notify parent component of updated status from server
        if (onStatusChange) {
          onStatusChange(serverEnabledState);
        }

        // Dispatch a custom event to notify other components about the change
        window.dispatchEvent(new Event('citation-settings-changed'));
      } else {
        // Revert to original state if save failed
        setCitationsEnabled(!newState);
        toast.error(data.message || 'Failed to save citation settings');
      }
    } catch (error) {
      // Revert to original state if save failed
      setCitationsEnabled(!newState);
      console.error('Error saving citation settings:', error);
      toast.error('Unable to save citation settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-3">
        <Loader2 className="animate-spin text-brand-500" size={24} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-1">
          Citation Settings
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Enable or disable citations in AI responses. When enabled, responses will include professionally formatted
          citations with numbered references in text ([1], [2], etc.) and a clean Sources section at the end,
          matching Claude's citation style.
        </p>

        <div className="flex items-center gap-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={citationsEnabled}
              onChange={handleToggleCitations}
              name="citations-enabled"
              disabled={saving}
            />
            <div className="w-11 h-6 bg-gray-200 peer-checked:bg-brand-500 rounded-full peer after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
          </label>
          <div className="flex items-center">
            <span className="text-sm font-medium">Enable Citations</span>
            {saving && <Loader2 className="animate-spin text-brand-500 ml-2" size={16} />}
          </div>
        </div>

        {citationsEnabled && (
          <div className="bg-white rounded-card border border-surface-border shadow-card p-4 mt-3">
            <h4 className="text-sm font-semibold mb-1">
              Citation Style
            </h4>
            <p className="text-sm text-gray-500">
              Citations will appear inline in the text as numbered references [1], [2], etc., with a formatted
              Sources section at the end of each response. All sources include titles and URLs when available.
            </p>

            <div className="mt-3">
              <h4 className="text-sm font-semibold mb-1">
                Example:
              </h4>
              <div className="p-3 bg-gray-50 rounded text-sm">
                "The latest advancements in machine learning involve transformer architectures [1], which have revolutionized
                natural language processing. Recent studies show significant improvements in performance metrics [2] compared to
                previous approaches."
                <div className="mt-3 pt-2 border-t border-gray-200 text-xs">
                  <strong>Sources</strong>
                  <ol className="mt-1 pl-5 list-decimal">
                    <li>arxiv.org - Attention Is All You Need (https://arxiv.org/abs/1706.03762)</li>
                    <li>openai.com - Language Models are Few-Shot Learners (https://openai.com/research/language-models-are-few-shot-learners)</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

CitationSettings.defaultProps = {
  onStatusChange: () => {}, // Default no-op function if not provided
  onSaveClick: () => {} // Default no-op function if not provided
};

export default CitationSettings;
